<template>
  <div class="phase-banner-root" aria-hidden="true">
    <div
      v-if="current"
      class="phase-banner"
      :class="[
        `tone-${toneOf(current.phase)}`,
        { opponent: current.opponent },
        `is-${motion}`,
      ]"
      :style="timingStyle"
    >
      <div class="phase-banner-ribbon" />
      <div class="phase-banner-flare" />
      <div class="phase-banner-copy">
        <span v-if="showEnglishTag" class="phase-banner-en">{{
          enLabel(current.phase)
        }}</span>
        <span class="phase-banner-title">{{ titleOf(current.phase) }}</span>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onUnmounted, ref, watch } from 'vue'
import { EnginePhase } from '@shared/index'
import { AnimationSpeed } from '../game/AnimationSpeed'
import { useI18n } from '../i18n/t'

const ENTER_MS = 420
const HOLD_MS = 780
const HOLD_QUEUED_MS = 200
const EXIT_MS = 320

const EN_LABEL: Record<EnginePhase, string> = {
  [EnginePhase.DRAW]: 'DRAW PHASE',
  [EnginePhase.STANDBY]: 'STANDBY PHASE',
  [EnginePhase.MAIN_1]: 'MAIN PHASE 1',
  [EnginePhase.BATTLE]: 'BATTLE PHASE',
  [EnginePhase.MAIN_2]: 'MAIN PHASE 2',
  [EnginePhase.END]: 'END PHASE',
}

const TITLE_KEY: Record<EnginePhase, string> = {
  [EnginePhase.DRAW]: 'duel.phaseBanner.draw',
  [EnginePhase.STANDBY]: 'duel.phaseBanner.standby',
  [EnginePhase.MAIN_1]: 'duel.phaseBanner.main1',
  [EnginePhase.BATTLE]: 'duel.phaseBanner.battle',
  [EnginePhase.MAIN_2]: 'duel.phaseBanner.main2',
  [EnginePhase.END]: 'duel.phaseBanner.end',
}

const TONE: Record<EnginePhase, string> = {
  [EnginePhase.DRAW]: 'draw',
  [EnginePhase.STANDBY]: 'standby',
  [EnginePhase.MAIN_1]: 'main',
  [EnginePhase.BATTLE]: 'battle',
  [EnginePhase.MAIN_2]: 'main',
  [EnginePhase.END]: 'end',
}

interface BannerJob {
  phase: EnginePhase
  opponent: boolean
}

const props = defineProps<{
  /** Current HUD phase. Null hides the banner (dice / game over). */
  phase: EnginePhase | null
  opponent: boolean
  /** Keep the banner on-screen in the hold pose (changelog shots). */
  pinned?: boolean
  /**
   * A decision prompt is up. Drop the splash at once so its exit
   * (about a second later) cannot slide through that card.
   */
  quiet?: boolean
}>()

const { t } = useI18n()

const queue = ref<BannerJob[]>([])
const current = ref<BannerJob | null>(null)
const motion = ref<'enter' | 'hold' | 'exit'>('enter')
let timer: ReturnType<typeof setTimeout> | null = null

const timingStyle = computed(() => ({
  '--enter': `${AnimationSpeed.getDuration(ENTER_MS)}ms`,
  '--exit': `${AnimationSpeed.getDuration(EXIT_MS)}ms`,
}))

function enLabel(phase: EnginePhase): string {
  return EN_LABEL[phase]
}

function titleOf(phase: EnginePhase): string {
  return t(TITLE_KEY[phase])
}

function toneOf(phase: EnginePhase): string {
  return TONE[phase]
}

const showEnglishTag = computed(() => {
  const job = current.value
  if (!job) return false
  return titleOf(job.phase).toUpperCase() !== enLabel(job.phase)
})

function clearTimer(): void {
  if (timer === null) return
  clearTimeout(timer)
  timer = null
}

function later(ms: number, fn: () => void): void {
  clearTimer()
  timer = setTimeout(fn, AnimationSpeed.getDuration(ms))
}

/** Drop a queued job that is the same phase as the last item. */
function sameAsTail(phase: EnginePhase): boolean {
  const tail = queue.value[queue.value.length - 1] ?? current.value
  return tail?.phase === phase
}

/**
 * Queue a phase splash. A new phase during the hold cuts the wait so the
 * strip can keep up when the player skips (M1 → EP).
 */
function enqueue(phase: EnginePhase, opponent: boolean): void {
  if (props.quiet) return
  if (sameAsTail(phase)) return
  queue.value.push({ phase, opponent })
  if (current.value && motion.value === 'hold') {
    beginExit()
    return
  }
  if (!current.value) pump()
}

function pump(): void {
  const next = queue.value.shift()
  if (!next) {
    current.value = null
    return
  }
  current.value = next
  motion.value = 'enter'
  later(ENTER_MS, () => {
    motion.value = 'hold'
    const hold = queue.value.length > 0 ? HOLD_QUEUED_MS : HOLD_MS
    later(hold, beginExit)
  })
}

function beginExit(): void {
  if (!current.value) {
    pump()
    return
  }
  motion.value = 'exit'
  later(EXIT_MS, () => {
    current.value = null
    pump()
  })
}

function reset(): void {
  clearTimer()
  queue.value = []
  current.value = null
}

watch(
  () => props.quiet,
  (quiet) => {
    if (quiet) reset()
  },
)

watch(
  () => props.phase,
  (phase, prev) => {
    if (props.quiet) {
      reset()
      return
    }
    if (props.pinned) {
      if (!phase) {
        reset()
        return
      }
      clearTimer()
      queue.value = []
      current.value = { phase, opponent: props.opponent }
      motion.value = 'hold'
      return
    }
    if (!phase) {
      reset()
      return
    }
    if (phase === prev) return
    enqueue(phase, props.opponent)
  },
)

onUnmounted(reset)
</script>

<style scoped>
.phase-banner-root {
  position: absolute;
  inset: 0;
  z-index: 175;
  pointer-events: none;
  overflow: hidden;
  user-select: none;
}

.phase-banner {
  position: absolute;
  left: 0;
  right: 0;
  top: 50%;
  transform: translateY(-132%);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 118px;
  padding: 10px 8%;
  --ink: #4ade80;
  --glow: rgba(74, 222, 128, 0.55);
}

.phase-banner.tone-draw {
  --ink: #7dd3fc;
  --glow: rgba(125, 211, 252, 0.55);
}

.phase-banner.tone-standby {
  --ink: #e2e8f0;
  --glow: rgba(226, 232, 240, 0.4);
}

.phase-banner.tone-main {
  --ink: #4ade80;
  --glow: rgba(74, 222, 128, 0.55);
}

.phase-banner.tone-battle {
  --ink: #fb7185;
  --glow: rgba(251, 113, 133, 0.6);
}

.phase-banner.tone-end {
  --ink: #c4b5fd;
  --glow: rgba(196, 181, 253, 0.5);
}

.phase-banner.opponent.tone-draw,
.phase-banner.opponent.tone-standby,
.phase-banner.opponent.tone-main,
.phase-banner.opponent.tone-end {
  --ink: #facc15;
  --glow: rgba(250, 204, 21, 0.55);
}

.phase-banner.opponent.tone-battle {
  --ink: #fb923c;
  --glow: rgba(251, 146, 60, 0.6);
}

.phase-banner-ribbon {
  position: absolute;
  inset: 0;
  background: linear-gradient(
    90deg,
    transparent 0%,
    rgba(2, 6, 23, 0.55) 10%,
    rgba(2, 6, 23, 0.82) 50%,
    rgba(2, 6, 23, 0.55) 90%,
    transparent 100%
  );
  box-shadow: 0 0 28px var(--glow);
  transform: scaleX(0);
  transform-origin: left center;
}

.phase-banner-ribbon::before,
.phase-banner-ribbon::after {
  content: '';
  position: absolute;
  left: 8%;
  right: 8%;
  height: 2px;
  background: linear-gradient(
    90deg,
    transparent,
    var(--ink) 18%,
    #fff 50%,
    var(--ink) 82%,
    transparent
  );
  opacity: 0.9;
}

.phase-banner-ribbon::before {
  top: 10px;
}

.phase-banner-ribbon::after {
  bottom: 10px;
}

.phase-banner-flare {
  position: absolute;
  inset: 18% -20%;
  background: repeating-linear-gradient(
    -18deg,
    transparent 0 14px,
    rgba(255, 255, 255, 0.05) 14px 16px
  );
  opacity: 0;
  pointer-events: none;
}

.phase-banner-copy {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  transform: skewX(-10deg);
  text-align: center;
}

.phase-banner-en {
  font-family: Cinzel, Georgia, serif;
  font-size: clamp(11px, 1.4vw, 16px);
  font-weight: 700;
  letter-spacing: 0.42em;
  color: var(--ink);
  opacity: 0.85;
  text-transform: uppercase;
}

.phase-banner-title {
  font-family: Impact, 'Arial Black', 'Arial Narrow', sans-serif;
  font-size: clamp(28px, 6.4vw, 76px);
  font-style: italic;
  font-weight: 700;
  line-height: 0.92;
  letter-spacing: 0.06em;
  color: var(--ink);
  text-transform: uppercase;
  white-space: nowrap;
  text-shadow:
    0 0 12px var(--glow),
    0 0 28px var(--glow),
    3px 3px 0 #020617,
    -1px -1px 0 #020617;
  -webkit-text-stroke: 1px rgba(2, 6, 23, 0.85);
}

.is-enter .phase-banner-ribbon {
  animation: ribbon-in var(--enter) cubic-bezier(0.16, 1, 0.3, 1) forwards;
}

.is-enter .phase-banner-copy {
  animation: copy-in var(--enter) cubic-bezier(0.16, 1, 0.3, 1) both;
}

.is-enter .phase-banner-flare {
  animation: flare-in var(--enter) ease-out both;
}

.is-hold .phase-banner-ribbon {
  transform: scaleX(1);
}

.is-hold .phase-banner-flare {
  opacity: 0.35;
}

.is-exit .phase-banner-ribbon {
  transform-origin: right center;
  animation: ribbon-out var(--exit) ease-in forwards;
}

.is-exit .phase-banner-copy {
  animation: copy-out var(--exit) ease-in both;
}

.is-exit .phase-banner-flare {
  animation: flare-out var(--exit) ease-in both;
}

@keyframes ribbon-in {
  from {
    transform: scaleX(0);
  }
  to {
    transform: scaleX(1);
  }
}

@keyframes ribbon-out {
  from {
    transform: scaleX(1);
  }
  to {
    transform: scaleX(0);
  }
}

@keyframes copy-in {
  0% {
    opacity: 0;
    transform: skewX(-10deg) translateX(-22%) scale(1.12);
    filter: brightness(2.2);
  }
  62% {
    opacity: 1;
    transform: skewX(-10deg) translateX(1.6%) scale(1);
    filter: brightness(1.15);
  }
  100% {
    opacity: 1;
    transform: skewX(-10deg) translateX(0) scale(1);
    filter: brightness(1);
  }
}

@keyframes copy-out {
  0% {
    opacity: 1;
    transform: skewX(-10deg) translateX(0);
    filter: brightness(1);
  }
  100% {
    opacity: 0;
    transform: skewX(-12deg) translateX(24%);
    filter: brightness(1.8);
  }
}

@keyframes flare-in {
  0% {
    opacity: 0;
    transform: translateX(-12%);
  }
  40% {
    opacity: 0.55;
  }
  100% {
    opacity: 0.35;
    transform: translateX(0);
  }
}

@keyframes flare-out {
  from {
    opacity: 0.35;
  }
  to {
    opacity: 0;
  }
}

@media (max-width: 720px) {
  .phase-banner-title {
    white-space: normal;
    font-size: clamp(26px, 8vw, 44px);
  }
}

/* Landscape phones. Blur + brightness filters repaint the whole duel during the turn splash. */
@media (max-height: 500px) and (max-width: 1100px) {
  .phase-banner-title {
    text-shadow: 2px 2px 0 #020617;
  }

  .is-enter .phase-banner-copy {
    animation-name: copy-in-flat;
  }

  .is-exit .phase-banner-copy {
    animation-name: copy-out-flat;
  }
}

@keyframes copy-in-flat {
  0% {
    opacity: 0;
    transform: skewX(-10deg) translateX(-22%) scale(1.12);
  }
  100% {
    opacity: 1;
    transform: skewX(-10deg) translateX(0) scale(1);
  }
}

@keyframes copy-out-flat {
  0% {
    opacity: 1;
    transform: skewX(-10deg) translateX(0);
  }
  100% {
    opacity: 0;
    transform: skewX(-12deg) translateX(24%);
  }
}

@media (prefers-reduced-motion: reduce) {
  .is-enter .phase-banner-ribbon,
  .is-enter .phase-banner-copy,
  .is-enter .phase-banner-flare,
  .is-exit .phase-banner-ribbon,
  .is-exit .phase-banner-copy,
  .is-exit .phase-banner-flare {
    animation: none;
  }

  .phase-banner-ribbon {
    transform: scaleX(1);
  }

  .phase-banner-copy {
    opacity: 1;
  }
}
</style>
